import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users as UsersIcon,
} from 'lucide-react';

const highlights = [
  {
    icon: TrendingUp,
    title: 'Real-time analytics',
    desc: 'Live dashboards that surface what actually moves the needle.',
  },
  {
    icon: UsersIcon,
    title: 'Built for teams',
    desc: 'Roles, permissions, and audit logs from day one.',
  },
  {
    icon: ShieldCheck,
    title: 'Enterprise-grade security',
    desc: 'Encrypted at rest, JWT sessions, rate-limited APIs.',
  },
];

export function AuthLayout() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 app-mesh opacity-80" />

      <div className="relative z-10 grid min-h-screen lg:grid-cols-2">
        {/* Brand panel */}
        <div className="relative hidden flex-col overflow-hidden bg-gradient-to-br from-primary-700 via-indigo-700 to-sky-600 p-10 text-white lg:flex">
          {/* Decorative blobs */}
          <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 -right-20 h-96 w-96 rounded-full bg-fuchsia-400/20 blur-3xl" />
          <div className="pointer-events-none absolute inset-0 bg-dot-grid opacity-40" />

          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative flex items-center gap-3"
          >
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-white/15 backdrop-blur-md">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold tracking-tight">VM Digital</p>
              <p className="text-xs text-white/70">Admin Console</p>
            </div>
          </motion.div>

          <div className="relative my-auto">
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl font-semibold leading-[1.1] tracking-tight"
            >
              Run your operations,
              <br />
              <span className="bg-gradient-to-r from-fuchsia-200 via-white to-sky-200 bg-clip-text text-transparent">
                beautifully simple.
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-4 max-w-md text-base text-white/80"
            >
              Manage users, attendance, tickets, and inventory in one
              clean, lightning-fast workspace your team will love.
            </motion.p>

            <ul className="mt-8 space-y-4">
              {highlights.map((h, i) => (
                <motion.li
                  key={h.title}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
                  className="flex items-start gap-3"
                >
                  <span className="mt-0.5 grid h-8 w-8 place-items-center rounded-lg bg-white/15 backdrop-blur-md">
                    <h.icon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">{h.title}</p>
                    <p className="text-xs text-white/70">{h.desc}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>

        </div>

        {/* Form panel */}
        <div className="relative flex items-center justify-center px-4 py-10 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-md"
          >
            {/* Mobile brand */}
            <div className="mb-8 flex items-center justify-center gap-2.5 lg:hidden">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-primary-500 to-indigo-600 text-white shadow-primary">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="text-left">
                <p className="text-base font-semibold leading-tight">
                  VM Digital
                </p>
                <p className="text-xs text-muted-foreground">Admin Console</p>
              </div>
            </div>
            <Outlet />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
